import React, { useEffect, useMemo, useState } from "react";
import {
  fetchUsers,
  fetchDepartments,
  bulkHolidayRequest,
  fetchEntitlements, // all users’ entitlements for a year
  fetchAllHolidayRequests, // all requests (we filter by user)
	fetchPublicHolidays,
} from "./api";
import { toast } from "react-toastify";

function BulkHolidayRequest() {
  const currentYear = new Date().getFullYear();

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDepartment, setFilterDepartment] = useState("");
  const [search, setSearch] = useState("");

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [entitlementsMap, setEntitlementsMap] = useState(new Map()); // userId -> entitlement row

  const [selectedUsers, setSelectedUsers] = useState([]); // array of userIds
  const [selectAll, setSelectAll] = useState(false);

  const [holidayDetails, setHolidayDetails] = useState({
    startDate: "",
    endDate: "",
    startDayType: "full",
    endDayType: "full",
  });

  const [preview, setPreview] = useState([]); // [{userId, name, remaining, required, overlap, ok, reason}]
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ----- Load users & departments
  useEffect(() => {
    const run = async () => {
      try {
        const [u, d] = await Promise.all([fetchUsers(), fetchDepartments()]);
        setUsers(u.data);
        setDepartments(d.data);
      } catch {
        toast.error("Failed to fetch users/departments");
      }
    };
    run();
  }, []);

  // ----- Load yearly entitlements
  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await fetchEntitlements(selectedYear);
        const map = new Map(data.map((e) => [e.user_id, e]));
        setEntitlementsMap(map);
      } catch {
        setEntitlementsMap(new Map());
        toast.error("Failed to fetch yearly entitlements");
      }
      // reset preview when year changes
      setPreview([]);
    };
    run();

    // clamp dates to the selected year
    const clamp = (iso) =>
      iso && new Date(iso).getFullYear() === Number(selectedYear) ? iso : "";
    setHolidayDetails((prev) => ({
      ...prev,
      startDate: clamp(prev.startDate),
      endDate: clamp(prev.endDate),
    }));
  }, [selectedYear]);

  // ----- Derived: filtered users
  const filteredUsers = useMemo(() => {
    let list = users;
    if (filterDepartment)
      list = list.filter((u) => u.department === filterDepartment);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          (u.firstName && u.firstName.toLowerCase().includes(q)) ||
          (u.lastName && u.lastName.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q))
      );
    }
    return list;
  }, [users, filterDepartment, search]);

  // ----- Select all (only on filtered)
  const toggleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedUsers(checked ? filteredUsers.map((u) => u.id) : []);
  };

  // ----- Individual selection
  const toggleUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ----- Helpers
  const isDateInRestrictedRange = (d) => {
    const year = d.getFullYear();
    const a = new Date(year, 11, 24); // Dec 24
    const b = new Date(year, 11, 31); // Dec 31
    a.setHours(0, 0, 0, 0);
    b.setHours(23, 59, 59, 999);
    const x = new Date(d);
    x.setHours(12, 0, 0, 0);
    return x >= a && x <= b;
  };

  const rangesOverlap = (s1, e1, s2, e2) =>
    new Date(s1) <= new Date(e2) && new Date(s2) <= new Date(e1);

  // Approximate required hours (Mon–Thu 10h, half day = 5h; ignores bank holidays -> backend authoritative)
  const estimateRequiredHours = async (startISO, endISO, startType, endType) => {
	  const start = new Date(startISO);
	  const end = new Date(endISO);
	  if (start > end) return 0;

	  const { data: holidays } = await fetchPublicHolidays(selectedYear, "GB", "EW");
	  const holidaySet = new Set(holidays.map((h) => h.holiday_date));

	  const isHoliday = (d) => holidaySet.has(d.toISOString().slice(0, 10));

	  // same day
	  if (start.toDateString() === end.toDateString()) {
		const dow = start.getDay();
		if (dow < 1 || dow > 4 || isHoliday(start)) return 0;
		return startType === "morning" || startType === "afternoon" ? 5 : 10;
	  }

	  let total = 0;
	  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
		const dow = d.getDay();
		if (dow < 1 || dow > 4 || isHoliday(d)) continue;

		let add = 10;
		const isStart = d.toDateString() === start.toDateString();
		const isEnd = d.toDateString() === end.toDateString();

		if (isStart) {
		  add = startType === "morning" || startType === "afternoon" ? 5 : 10;
		}
		if (isEnd && !isStart) {
		  add = endType === "morning" || endType === "afternoon" ? 5 : 10;
		}

		total += add;
	  }

	  return total;
	};

  // ----- Preview button
  const handlePreview = async () => {
    const { startDate, endDate, startDayType, endDayType } = holidayDetails;

    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    const s = new Date(startDate);
    const e = new Date(endDate);

    if (
      s.getFullYear() !== Number(selectedYear) ||
      e.getFullYear() !== Number(selectedYear)
    ) {
      toast.error(`Dates must be within ${selectedYear}.`);
      return;
    }
	  /*
    if (isDateInRestrictedRange(s) || isDateInRestrictedRange(e)) {
      toast.error("Dates may not be between December 24th and 31st inclusive.");
      return;
    }*/
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user.");
      return;
    }

    setIsPreviewing(true);
    try {
      const required = await estimateRequiredHours(
        startDate,
        endDate,
        startDayType,
        endDayType
      );

      // load all requests and test overlaps user-by-user in that year
      const { data: allReqs } = await fetchAllHolidayRequests(); // must include hr.user_id
      const rows = selectedUsers.map((uid) => {
        const ent = entitlementsMap.get(uid);
        const remaining = Math.max(ent?.remaining_hours ?? 0, 0);

        const overlap = allReqs.some((r) => {
          if (["cancelled", "rejected"].includes(r.status)) return false;
          if (Number(r.user_id) !== Number(uid)) return false;
          const sameYear =
            new Date(r.start_date).getFullYear() === Number(selectedYear) ||
            new Date(r.end_date).getFullYear() === Number(selectedYear);
          if (!sameYear) return false;
          return rangesOverlap(r.start_date, r.end_date, startDate, endDate);
        });

        let ok = true;
        let reason = "";
        if (overlap) {
          ok = false;
          reason = "Overlaps existing request";
        } else if (remaining < required) {
          ok = false;
          reason = `Not enough remaining (${remaining}h < ${required}h)`;
        }

        const u = users.find((x) => x.id === uid);
        return {
          userId: uid,
          name: u ? `${u.firstName} ${u.lastName}` : `#${uid}`,
          remaining,
          required,
          overlap,
          ok,
          reason,
        };
      });

      setPreview(rows);
      const okCount = rows.filter((r) => r.ok).length;
      if (okCount === 0) {
        toast.warn(
          "No eligible users (all have overlaps or insufficient hours)."
        );
      } else if (okCount < rows.length) {
        toast.info(
          `${okCount}/${rows.length} users are eligible. Others will be skipped.`
        );
      } else {
        toast.success("All selected users are eligible.");
      }
    } catch (err) {
      toast.error("Failed to build preview");
    } finally {
      setIsPreviewing(false);
    }
  };

  // ----- Submit (only OK subset)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!preview.length) {
      toast.error("Please click Preview first.");
      return;
    }
    const okUserIds = preview.filter((r) => r.ok).map((r) => r.userId);
    if (!okUserIds.length) {
      toast.error("No eligible users to submit.");
      return;
    }

    setIsSubmitting(true);
    try {
      await bulkHolidayRequest({
        userIds: okUserIds,
        startDate: holidayDetails.startDate,
        endDate: holidayDetails.endDate,
        startDayType: holidayDetails.startDayType,
        endDayType: holidayDetails.endDayType,
      });

      toast.success(`Submitted for ${okUserIds.length} user(s).`);

      // refresh entitlements for the year
      const { data } = await fetchEntitlements(selectedYear);
      setEntitlementsMap(new Map(data.map((e) => [e.user_id, e])));

      // clear selection & preview
      setSelectedUsers([]);
      setSelectAll(false);
      setPreview([]);
    } catch {
      toast.error("Failed to submit bulk holiday request");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ----- UI
  const onDetailChange = (field, value) =>
    setHolidayDetails((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-semibold mb-6 text-center">
        Bulk Holiday Request
      </h1>

      {/* Top controls */}
      <div className="bg-white p-4 rounded shadow mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-gray-700 font-bold mb-2">Year</label>
          <input
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-bold mb-2">
            Filter by Department
          </label>
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-bold mb-2">Search</label>
          <input
            type="text"
            placeholder="Name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
      </div>

      {/* Dates / Types */}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-4 rounded shadow mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={holidayDetails.startDate}
              onChange={(e) => onDetailChange("startDate", e.target.value)}
              min={`${selectedYear}-01-01`}
              max={`${selectedYear}-12-31`}
              className="w-full border rounded px-3 py-2"
              required
            />
            <div className="mt-2">
              <select
                value={holidayDetails.startDayType}
                onChange={(e) => onDetailChange("startDayType", e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="full">Full Day</option>
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-bold mb-2">
              End Date
            </label>
            <input
              type="date"
              value={holidayDetails.endDate}
              onChange={(e) => onDetailChange("endDate", e.target.value)}
              min={holidayDetails.startDate || `${selectedYear}-01-01`}
              max={`${selectedYear}-12-31`}
              className="w-full border rounded px-3 py-2"
              required
            />
            <div className="mt-2">
              <select
                value={holidayDetails.endDayType}
                onChange={(e) => onDetailChange("endDayType", e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="full">Full Day</option>
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users list */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-gray-700 font-bold">
              Select Users ({selectedUsers.length} selected)
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => toggleSelectAll(e.target.checked)}
                className="form-checkbox"
              />
              <span className="ml-2">Select All (filtered)</span>
            </label>
          </div>

          <div className="border rounded p-2 max-h-72 overflow-y-auto">
            {filteredUsers.map((u) => {
              const ent = entitlementsMap.get(u.id);
              const remaining = Math.max(ent?.remaining_hours ?? 0, 0);
              return (
                <div
                  key={u.id}
                  className="flex items-center justify-between py-1"
                >
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="form-checkbox"
                    />
                    <span className="ml-2">
                      {u.firstName} {u.lastName} ({u.email})
                    </span>
                  </label>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      remaining > 0
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-700"
                    }`}
                    title={`Remaining in ${selectedYear}`}
                  >
                    {remaining}h left
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
          <button
            type="button"
            onClick={handlePreview}
            className="w-full md:w-auto bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded"
            disabled={isPreviewing}
          >
            {isPreviewing ? "Building Preview…" : "Preview"}
          </button>

          <button
            type="submit"
            className="w-full md:w-auto bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            disabled={isSubmitting || !preview.length}
          >
            {isSubmitting ? "Submitting…" : "Submit (OK users only)"}
          </button>
        </div>
      </form>

      {/* Preview table */}
      {!!preview.length && (
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-xl font-semibold mb-3">Preview</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border">User</th>
                  <th className="px-3 py-2 text-right border">
                    Required (est.)
                  </th>
                  <th className="px-3 py-2 text-right border">Remaining</th>
                  <th className="px-3 py-2 text-left border">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r) => (
                  <tr key={r.userId} className="border-t">
                    <td className="px-3 py-2 border">{r.name}</td>
                    <td className="px-3 py-2 border text-right">
                      {r.required}h
                    </td>
                    <td className="px-3 py-2 border text-right">
                      {r.remaining}h
                    </td>
                    <td className="px-3 py-2 border">
                      {r.ok ? (
                        <span className="text-green-700 font-semibold">OK</span>
                      ) : (
                        <span className="text-red-700 font-semibold">
                          {r.reason}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-500 mt-2">
              Required hours are estimated (Mon–Thu only). Bank holidays are
              excluded by the backend.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkHolidayRequest;
